import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  size?: 'sm' | 'md' | 'lg';
  onSearch?: (value: string) => void;
  onChange?: (value: string) => void;
  onClear?: () => void;
  debounceMs?: number;
  showClear?: boolean;
  loading?: boolean;
  variant?: 'default' | 'filled' | 'ghost';
}

const sizeStyles: Record<string, { input: string; icon: string }> = {
  sm: { input: 'pl-9 pr-8 py-2 text-sm', icon: 'w-4 h-4 left-3' },
  md: { input: 'pl-10 pr-9 py-2.5 text-sm', icon: 'w-5 h-5 left-3' },
  lg: { input: 'pl-12 pr-10 py-3 text-base', icon: 'w-5 h-5 left-4' },
};

const variantStyles: Record<string, string> = {
  default: 'bg-white border border-neutral-300 focus:border-primary-500 focus:ring-primary-500/20',
  filled: 'bg-neutral-100 border border-transparent hover:bg-neutral-200 focus:bg-white focus:border-primary-500 focus:ring-primary-500/20',
  ghost: 'bg-transparent border-0 hover:bg-neutral-50 focus:bg-neutral-50',
};

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      size = 'md',
      onSearch,
      onChange,
      onClear,
      debounceMs = 300,
      showClear = true,
      loading = false,
      variant = 'default',
      placeholder = 'Search...',
      className,
      value: controlledValue,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const value = controlledValue !== undefined ? String(controlledValue) : internalValue;
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const styles = sizeStyles[size];

    useEffect(() => {
      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      
      onChange?.(newValue);

      if (onSearch && debounceMs > 0) {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
          onSearch(newValue);
        }, debounceMs);
      } else if (onSearch) {
        onSearch(newValue);
      }
    };

    const handleClear = () => {
      if (controlledValue === undefined) {
        setInternalValue('');
      }
      onChange?.('');
      onSearch?.('');
      onClear?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSearch) {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        onSearch(value);
      }
      if (e.key === 'Escape') {
        handleClear();
      }
    };

    return (
      <div className={clsx('relative', className)}>
        {/* Search Icon */}
        <div className={clsx('absolute top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none', styles.icon)}>
          {loading ? (
            <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <MagnifyingGlassIcon className="w-full h-full" />
          )}
        </div>

        {/* Input */}
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={clsx(
            'w-full rounded-lg transition-all duration-200',
            'focus:outline-none focus:ring-2',
            styles.input,
            variantStyles[variant]
          )}
          {...props}
        />

        {/* Clear Button */}
        {showClear && value && (
          <button
            type="button"
            onClick={handleClear}
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2',
              'text-neutral-400 hover:text-neutral-600',
              'rounded-full p-0.5 hover:bg-neutral-100',
              'transition-colors duration-150'
            )}
          >
            <XMarkIcon className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;

